import AppContext from 'App/AppContext';
import { CopilotLibraryEditorContextKey } from 'models/copilot/copilotBridge';
import { CopilotConversationContentBlock } from 'models/copilot/copilotConversation';
import {
  CopilotDocumentIngest,
  CopilotDocumentIngestSourceType,
  CopilotDocumentIngestStatus,
  CopilotDocumentStorageSourceType,
} from 'models/copilot/copilotDocumentIngest';
import { DocumentProviderCapabilities, getListCapabilities } from 'models/documentProvider';
import ResourceLink from 'models/resourceLink';
import { ResourceListType } from 'models/resourceList';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiCopilotReferenceLibraryDocument } from 'services/Api/copilotService';
import { apiRequest } from 'services/Auth/authConfig';
import AppError from 'utils/appError';
import { CopilotAgentSessionSeed } from '../../Agent/useCopilotAgentSession';
import { getDocumentBlockFamilies } from './copilotDocumentBlockHelpers';
import { CopilotDocumentEditModal } from './CopilotDocumentEditModal';
import { isStaleLibraryDocumentIngestResult } from './copilotDocumentIngestErrors';
import { useLinkWriteAccess } from './useCopilotDocumentWriteAccess';

export type LibraryDocumentEditType = 'docx' | 'page';

// Why the Edit action is unavailable: 'type' = not an editable document type, 'noaccess' = no SharePoint write
// permission, 'busy' = the access check or an editor session is still being prepared. Undefined = available.
export type LibraryDocumentEditDisabledReason = 'type' | 'noaccess' | 'busy' | undefined;

// docx (a .docx file in a document library) and SharePoint pages are the only library document types we have an
// inline editor for. Returns undefined for anything else (PDF/other office files, custom lists, web URLs, whole
// lists). The docx detection lives on the model (ResourceLink.isWordDocument), shared with canExportWordEvidence.
export const getLibraryDocumentEditType = (
  link: ResourceLink | undefined,
  capabilities?: DocumentProviderCapabilities,
): LibraryDocumentEditType | undefined => {
  if (!link || link.isWholeList() || !link.list) return undefined;

  // The editor saves straight back to SharePoint through Graph, and a page is edited through
  // its web parts. A document held by another provider is read-only until the editor can save
  // through the broker, so those providers report docxEdit and pageEdit as false.
  if (link.isWordDocument() && capabilities?.docxEdit !== false) {
    return 'docx';
  }

  if (
    link.list.listType === ResourceListType.SitePageLibrary &&
    !!link.pageId &&
    capabilities?.pageEdit !== false
  ) {
    return 'page';
  }

  return undefined;
};

export interface CopilotLibraryDocumentEditor {
  // Why the Edit action is unavailable, or undefined when it can be used. The caller maps this to a tooltip.
  disabledReason: LibraryDocumentEditDisabledReason;
  // Reference the document into a fresh Copilot session and open the edit modal. No-op while disabled.
  beginEdit: () => void;
  // The edit modal element, or null when closed.
  editorModal: JSX.Element | null;
}

// Drives the Copilot document Edit action for a library preview toolbar: gates by editable type and SharePoint
// write access (via the shared useLinkWriteAccess check), and on demand references the document into a new
// Copilot session so the shared CopilotDocumentEditModal can edit it (docx loads via the session, a page loads
// from the link). Edits persist straight to SharePoint, so the ephemeral session is simply dropped on close.
// Pass options.enabled=false when the toolbar is hidden (e.g. the preview embeds with hideCommandbar) so the
// hook skips the write-access check rather than doing work whose result is never shown.
export const useCopilotLibraryDocumentEditor = (
  link: ResourceLink | undefined,
  options?: {
    enabled?: boolean;
    onDocumentSaved?: () => void;
    onEditorOpenChange?: (isOpen: boolean) => void;
    // Submitted as the first question once the session is up, so the editor opens on the work instead
    // of on an empty box.
    pendingInstruction?: string;
  },
): CopilotLibraryDocumentEditor => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['copilot']);
  const editType = getLibraryDocumentEditType(link, getListCapabilities(link?.list, appContext.globalDataCache));
  const linkId = link?.linkId;
  const enabled = options?.enabled ?? true;
  const onDocumentSaved = options?.onDocumentSaved;
  const pendingInstruction = options?.pendingInstruction;
  const onEditorOpenChange = options?.onEditorOpenChange;

  const [isPreparing, setIsPreparing] = useState<boolean>(false);
  // The block and the session it was referenced into always travel together. Hold them in one state object so
  // the edit modal can never mount with the block but no session: the modal seeds useCopilotAgentSession's
  // copilotSessionId from the session once on mount, and a missing session there leaves the docx editor unable
  // to load (it loads by sessionId + blockId). Two separate state setters would not be batched here (they run
  // after an await under the legacy ReactDOM.render root), so the modal would otherwise mount mid-update.
  const [editorTarget, setEditorTarget] = useState<
    { block: CopilotConversationContentBlock; session: CopilotAgentSessionSeed } | undefined
  >(undefined);

  // Resolve SharePoint write access for editable documents (no-op while disabled or for non-editable types).
  const { canEdit, isCheckingAccess } = useLinkWriteAccess(linkId, enabled && !!editType);

  useEffect(() => {
    onEditorOpenChange?.(editorTarget !== undefined);

    return () => onEditorOpenChange?.(false);
  }, [editorTarget, onEditorOpenChange]);

  const disabledReason: LibraryDocumentEditDisabledReason = !editType
    ? 'type'
    : isPreparing || isCheckingAccess
    ? 'busy'
    : !canEdit
    ? 'noaccess'
    : undefined;

  const beginEdit = () => {
    if (!link || !editType || disabledReason) return;

    void (async () => {
      try {
        setIsPreparing(true);
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        const ingestRequest = new CopilotDocumentIngest();
        ingestRequest.contextKey = CopilotLibraryEditorContextKey;
        ingestRequest.sourceType = CopilotDocumentIngestSourceType.Library;
        ingestRequest.storageSourceType = CopilotDocumentStorageSourceType.SharePoint;
        ingestRequest.libraryLinkId = link.linkId;
        ingestRequest.libraryListId = link.listId;
        ingestRequest.title = link.linkName;

        const result = await apiCopilotReferenceLibraryDocument(ingestRequest, accessToken);
        const content = result?.content;
        if (isStaleLibraryDocumentIngestResult(result)) return;

        if (
          result?.status !== CopilotDocumentIngestStatus.Ready ||
          !content?.conversation ||
          content.sessionId === undefined
        ) {
          appContext.showNotification(result?.message || t('copilot:Chat.Document.IngestFailed'), true);

          return;
        }

        const block = getDocumentBlockFamilies(content.conversation)[0]?.latest;
        if (!block) {
          appContext.showNotification(t('copilot:Chat.Document.IngestFailed'), true);

          return;
        }

        setEditorTarget({
          block,
          session: { sessionId: content.sessionId, conversation: content.conversation },
        });
      } catch (err) {
        appContext.showNotification(
          AppError.fromApiError(err).message || t('copilot:Chat.Document.IngestFailed'),
          true,
        );
      } finally {
        setIsPreparing(false);
      }
    })();
  };

  const closeEditor = (hasSavedChanges: boolean) => {
    setEditorTarget(undefined);
    if (hasSavedChanges) onDocumentSaved?.();
  };

  const editorModal =
    editorTarget !== undefined ? (
      <CopilotDocumentEditModal
        isOpen={true}
        onClose={closeEditor}
        block={editorTarget.block}
        session={editorTarget.session}
        pendingInstruction={pendingInstruction}
      />
    ) : null;

  return { disabledReason, beginEdit, editorModal };
};
